/**
 * @fileoverview Edit User placement hook.
 * @description The Edit dialog's role and reporting-manager picker data: the role choices and the
 * manager choices for the chosen role (from GET /users/{userId}/edit-options), the translated
 * errors, and the role-change handler that clears the manager (one is only valid for the role it
 * was picked for).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useUserEditOptions } from '@/features/users/hooks/useUserEditOptions'

import type { EditUserFormValues } from '@/features/users/schemas/edit-user-form.schema'
import type { UseFormReturn } from 'react-hook-form'

/**
 * @description Loads the placement choices for the form's chosen role.
 * @param {UseFormReturn<EditUserFormValues>} form - The edit form.
 * @param {string} userId - The user being edited.
 * @returns {object} The role and manager options, the load state, the translated errors and the
 * role-change handler.
 */
export function useEditUserPlacement(form: UseFormReturn<EditUserFormValues>, userId: string) {
  const { t } = useTranslation()
  const { errors } = form.formState
  const primaryRoleId = useWatch({ control: form.control, name: 'primaryRoleId' })
  const { rolesQuery, managersQuery } = useUserEditOptions(userId, primaryRoleId, true)
  const managers = managersQuery.data ?? []

  /**
   * @description Clears the manager when the role changes: one is only valid for the role it was
   * picked for.
   */
  const onRoleChange = () => {
    form.setValue('reportingManagerUserId', '')
  }

  return {
    roles: rolesQuery.data ?? [],
    managers,
    noManagersAvailable: managersQuery.isSuccess && managers.length === 0,
    optionsError: rolesQuery.error ?? managersQuery.error,
    roleError: errors.primaryRoleId?.message && t(errors.primaryRoleId.message),
    managerError:
      errors.reportingManagerUserId?.message && t(errors.reportingManagerUserId.message),
    onRoleChange,
  }
}
